import logoPlain from '@/assets/logo-plain.png';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileSetup } from '@/hooks/useProfileSetup';
import { useRoleRequest } from '@/hooks/useRoleRequest';
import { useSmartCollapse } from '@/hooks/useSmartCollapse';
import { detectOverflow } from '@/utils/overflowDetection';
import { scrollIntoViewIfNeeded } from '@/utils/scrollHelpers';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Award, BarChart3, Calendar, ChevronDown, ClipboardList, CreditCard, Database, DollarSign, FileText, GitBranch, GripVertical, LogOut, Package, RefreshCw, Settings, Shield, Target, Trophy, UserCog, Users, Workflow, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
interface MenuItem {
  id: string;
  title: string;
  url: string;
  icon: LucideIcon;
  show: boolean;
}
interface SortableMenuItemProps {
  item: MenuItem;
  isActive: boolean;
  onClick: () => void;
  itemRef?: (element: HTMLElement | null) => void;
}
interface AdminSection {
  id: string;
  title: string;
  icon: LucideIcon;
  items: MenuItem[];
}
interface SortableAdminSectionProps {
  section: AdminSection;
  isActive: (path: string) => boolean;
  navigate: (url: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  sectionRef?: (element: HTMLElement | null) => void;
  onItemClick?: (url: string, itemId: string) => void;
  registerMenuItemRef?: (itemId: string) => (element: HTMLElement | null) => void;
}
/**
 * Sortable admin section with smart collapse behavior
 * @param section - The admin section configuration
 * @param isActive - Function to check if a route is active
 * @param navigate - React Router navigation function
 * @param onDragEnd - Handler for drag-and-drop reordering
 * @param isOpen - Controlled open state from useSmartCollapse
 * @param onOpenChange - Handler for open/close state changes
 * @param sectionRef - Callback ref for registering section element
 */
function SortableAdminSection({
  section,
  isActive,
  navigate,
  onDragEnd,
  isOpen,
  onOpenChange,
  sectionRef,
  onItemClick,
  registerMenuItemRef
}: SortableAdminSectionProps) {
  const {
    open
  } = useSidebar();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: section.id
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));

  const collapsibleRef = useRef<HTMLDivElement>(null);

  // Register ref with parent
  useEffect(() => {
    if (sectionRef && collapsibleRef.current) {
      sectionRef(collapsibleRef.current);
    }
  }, [sectionRef]);

  return <div ref={setNodeRef} style={style}>
      <Collapsible 
        open={isOpen} 
        onOpenChange={onOpenChange}
        className={`group/${section.id}`}
      >
        <div ref={collapsibleRef} data-section-id={section.id}>
        <SidebarGroup className="p-0 py-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <CollapsibleTrigger className="group relative flex w-full items-center justify-between hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[state=open]:bg-transparent rounded-md transition-colors h-10 text-sm font-semibold px-2">
                {open && <div {...attributes} {...listeners} className="absolute left-2 top-0 bottom-0 flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </div>}
                <div className={`flex items-center gap-2 ${open ? 'ml-6' : 'ml-0'} ${!open ? 'justify-center w-full' : ''}`}>
                  <section.icon className="h-4 w-4 shrink-0" />
                  {open && <span>{section.title}</span>}
                </div>
                {open && <ChevronDown className={`h-4 w-4 mr-2 transition-transform group-data-[state=open]/${section.id}:rotate-180 shrink-0`} />}
              </CollapsibleTrigger>
            </TooltipTrigger>
            {!open && (
              <TooltipContent side="right" align="center">
                {section.title}
              </TooltipContent>
            )}
          </Tooltip>
          <CollapsibleContent>
            <SidebarGroupContent>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={section.items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                  <SidebarMenu className="space-y-1 pl-4 ml-2 border-l border-sidebar-border">
                    {section.items.map(item => <SortableMenuItem key={item.id} item={item} isActive={isActive(item.url)} onClick={() => onItemClick ? onItemClick(item.url, item.id) : navigate(item.url)} itemRef={registerMenuItemRef ? registerMenuItemRef(item.id) : undefined} />)}
                  </SidebarMenu>
                </SortableContext>
              </DndContext>
            </SidebarGroupContent>
          </CollapsibleContent>
        </SidebarGroup>
        </div>
      </Collapsible>
    </div>;
}
/**
 * Sortable menu item component with drag handle
 */
function SortableMenuItem({
  item,
  isActive,
  onClick,
  itemRef
}: SortableMenuItemProps) {
  const {
    open
  } = useSidebar();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: item.id
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Register ref with parent
  useEffect(() => {
    if (itemRef && buttonRef.current) {
      itemRef(buttonRef.current);
    }
  }, [itemRef]);
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  
  return <SidebarMenuItem ref={setNodeRef} style={style}>
      <SidebarMenuButton ref={buttonRef} onClick={onClick} isActive={isActive} tooltip={item.title} className="group relative h-10">
        {open && <div {...attributes} {...listeners} className="absolute left-2 top-0 bottom-0 flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>}
        <item.icon className={`h-4 w-4 ${open ? 'ml-6' : ''}`} />
        <span>{item.title}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>;
}
/**
 * AppSidebar with smart collapsible sections
 * 
 * Features:
 * - Auto-collapse LRU (Least Recently Used) sections when overflow detected
 * - Smooth scroll to keep expanded sections visible
 * - Drag-and-drop reordering for menu items and sections
 * - Role-based navigation and permissions
 * - ARIA live regions for accessibility announcements
 * 
 * Smart Collapse Behavior:
 * - Enabled by default for all admin sections
 * - Automatically collapses oldest-used sections when a new section would overflow
 * - Uses ResizeObserver to handle dynamic content changes
 * - Maintains smooth scrolling to keep expanded content visible
 * 
 * Configuration:
 * - Smart collapse can be disabled by modifying the `enabled` option in useSmartCollapse
 * - Collapse strategy: incremental (collapses one section at a time until fit)
 * - Scroll behavior: smooth
 * - Debounce: 150ms for content changes
 */
export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signOut,
    hasPermission,
    isImpersonating,
    userRole,
    user
  } = useAuth();
  const {
    open,
    setOpen
  } = useSidebar();
  const {
    profile
  } = useProfileSetup();
  const {
    pendingRequests
  } = useRoleRequest();
  const userInitials = profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || user?.email?.[0].toUpperCase() || '?';
  const defaultNavigationItems: MenuItem[] = [{
    id: 'appointments',
    title: 'Appointments',
    url: '/appointments',
    icon: Calendar,
    show: true
  }, {
    id: 'configurator',
    title: 'Sales Configurator',
    url: '/configurator',
    icon: DollarSign,
    show: true
  }, {
    id: 'sales',
    title: 'My Sales',
    url: '/quotes',
    icon: Users,
    show: true
  }, {
    id: 'leaderboard',
    title: 'Sales Leaderboard',
    url: '/leaderboard',
    icon: Trophy,
    show: true
  }, {
    id: 'compare',
    title: 'Compare Plans',
    url: '/compare-plans',
    icon: BarChart3,
    show: true
  }].filter(item => item.show);

  // Load menu order from localStorage or use default
  const [navigationItems, setNavigationItems] = useState<MenuItem[]>(() => {
    const savedOrder = localStorage.getItem('menuOrder');
    if (savedOrder) {
      try {
        const orderIds = JSON.parse(savedOrder);
        const orderedItems = orderIds.map((id: string) => defaultNavigationItems.find(item => item.id === id)).filter(Boolean);

        // Add any new items that weren't in saved order
        const existingIds = new Set(orderIds);
        const newItems = defaultNavigationItems.filter(item => !existingIds.has(item.id));
        return [...orderedItems, ...newItems];
      } catch {
        return defaultNavigationItems;
      }
    }
    return defaultNavigationItems;
  });

  // Initialize admin section items with saved order
  const initializeAdminItems = (items: MenuItem[], storageKey: string): MenuItem[] => {
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderIds = JSON.parse(savedOrder);
        const orderedItems = orderIds.map((id: string) => items.find(item => item.id === id)).filter(Boolean);
        const existingIds = new Set(orderIds);
        const newItems = items.filter(item => !existingIds.has(item.id));
        return [...orderedItems, ...newItems];
      } catch {
        return items;
      }
    }
    return items;
  };
  const defaultSalesAdminItems: MenuItem[] = [{
    id: 'sales-flow',
    title: 'Sales Flow',
    url: '/admin/sales-flow',
    icon: Workflow,
    show: true
  }, {
    id: 'workflow-viz',
    title: 'Workflow Viz',
    url: '/admin/workflow',
    icon: GitBranch,
    show: true
  }, {
    id: 'sales-policies',
    title: 'Sales Policies',
    url: '/admin/policies',
    icon: FileText,
    show: true
  }, {
    id: 'pain-points',
    title: 'Pain Points',
    url: '/admin/pain-points',
    icon: Target,
    show: true
  }, {
    id: 'discount-codes',
    title: 'Approval Codes',
    url: '/admin/discount-codes',
    icon: Shield,
    show: true
  }, {
    id: 'sales-leaderboard',
    title: 'Sales Leaderboard',
    url: '/leaderboard',
    icon: Trophy,
    show: true
  }, {
    id: 'leaderboard-settings',
    title: 'Leaderboard Settings',
    url: '/admin/leaderboard',
    icon: Settings,
    show: true
  }, {
    id: 'appointments',
    title: 'Appointments',
    url: '/admin/appointments',
    icon: ClipboardList,
    show: true
  }, {
    id: 'towing-report',
    title: 'Towing Report',
    url: '/admin/towing-report',
    icon: BarChart3,
    show: true
  }];
  const defaultProductAdminItems: MenuItem[] = [{
    id: 'products',
    title: 'Products',
    url: '/admin/products',
    icon: Package,
    show: true
  }, {
    id: 'tier-features',
    title: 'Tier Features',
    url: '/admin/tier-features',
    icon: Award,
    show: true
  }];
  const defaultPaymentsAdminItems: MenuItem[] = [{
    id: 'stripe-payments',
    title: 'Stripe Payments',
    url: '/admin/payments',
    icon: CreditCard,
    show: true
  }];
  const defaultUserAdminItems: MenuItem[] = [{
    id: 'users',
    title: 'Users',
    url: '/admin/users',
    icon: UserCog,
    show: true
  }, {
    id: 'ghl-sync',
    title: 'GHL Sync',
    url: '/admin/ghl-sync',
    icon: RefreshCw,
    show: true
  }, {
    id: 'role-requests',
    title: 'Role Requests',
    url: '/admin/role-requests',
    icon: Users,
    show: true
  }, {
    id: 'role-manager',
    title: 'Role Manager',
    url: '/admin/role-manager',
    icon: Shield,
    show: true
  }, {
    id: 'audit-logs',
    title: 'Audit Logs',
    url: '/admin/audit-logs',
    icon: Shield,
    show: true
  }, {
    id: 'database',
    title: 'Database Export',
    url: '/admin/database',
    icon: Database,
    show: true
  }];
  const [salesAdminItems, setSalesAdminItems] = useState<MenuItem[]>(() => initializeAdminItems(defaultSalesAdminItems, 'salesAdminOrder'));
  const [productAdminItems, setProductAdminItems] = useState<MenuItem[]>(() => initializeAdminItems(defaultProductAdminItems, 'productAdminOrder'));
  const [paymentsAdminItems, setPaymentsAdminItems] = useState<MenuItem[]>(() => initializeAdminItems(defaultPaymentsAdminItems, 'paymentsAdminOrder'));
  const [userAdminItems, setUserAdminItems] = useState<MenuItem[]>(() => initializeAdminItems(defaultUserAdminItems, 'userAdminOrder'));

  // Admin sections with saved order
  const defaultAdminSections: AdminSection[] = [{
    id: 'sales-admin',
    title: 'Sales Admin',
    icon: DollarSign,
    items: salesAdminItems
  }, {
    id: 'product-admin',
    title: 'Product Admin',
    icon: Package,
    items: productAdminItems
  }, {
    id: 'payments-admin',
    title: 'Payments Admin',
    icon: CreditCard,
    items: paymentsAdminItems
  }, {
    id: 'user-admin',
    title: 'User Admin',
    icon: UserCog,
    items: userAdminItems
  }];
  const [adminSections, setAdminSections] = useState<AdminSection[]>(() => {
    const savedOrder = localStorage.getItem('adminSectionsOrder');
    if (savedOrder) {
      try {
        const orderIds = JSON.parse(savedOrder);
        const orderedSections = orderIds.map((id: string) => defaultAdminSections.find(section => section.id === id)).filter(Boolean);
        const existingIds = new Set(orderIds);
        const newSections = defaultAdminSections.filter(section => !existingIds.has(section.id));
        return [...orderedSections, ...newSections];
      } catch {
        return defaultAdminSections;
      }
    }
    return defaultAdminSections;
  });

  // Update admin sections when items change
  useEffect(() => {
    setAdminSections(prev => prev.map(section => {
      if (section.id === 'sales-admin') return {
        ...section,
        items: salesAdminItems
      };
      if (section.id === 'product-admin') return {
        ...section,
        items: productAdminItems
      };
      if (section.id === 'payments-admin') return {
        ...section,
        items: paymentsAdminItems
      };
      if (section.id === 'user-admin') return {
        ...section,
        items: userAdminItems
      };
      return section;
    }));
  }, [salesAdminItems, productAdminItems, paymentsAdminItems, userAdminItems]);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      setNavigationItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Save to localStorage
        localStorage.setItem('menuOrder', JSON.stringify(newOrder.map(item => item.id)));
        return newOrder;
      });
    }
  };
  const handleAdminSectionDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      setAdminSections(sections => {
        const oldIndex = sections.findIndex(section => section.id === active.id);
        const newIndex = sections.findIndex(section => section.id === over.id);
        const newOrder = arrayMove(sections, oldIndex, newIndex);
        localStorage.setItem('adminSectionsOrder', JSON.stringify(newOrder.map(section => section.id)));
        return newOrder;
      });
    }
  };
  const handleAdminDragEnd = (event: DragEndEvent, sectionId: string) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      const setItems = sectionId === 'sales-admin' ? setSalesAdminItems : sectionId === 'product-admin' ? setProductAdminItems : sectionId === 'payments-admin' ? setPaymentsAdminItems : setUserAdminItems;
      const storageKey = sectionId === 'sales-admin' ? 'salesAdminOrder' : sectionId === 'product-admin' ? 'productAdminOrder' : sectionId === 'payments-admin' ? 'paymentsAdminOrder' : 'userAdminOrder';
      setItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem(storageKey, JSON.stringify(newOrder.map(item => item.id)));
        return newOrder;
      });
    }
  };
  const showAdminNav = hasPermission('canManageProducts') && !isImpersonating;
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Smart collapse for sidebar sections
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const [ariaLiveMessage, setAriaLiveMessage] = useState<string>('');
  
  // Determine which admin section should be open based on current route
  // This is reactive to route changes via useMemo
  const getSectionForRoute = useCallback((pathname: string): string | undefined => {
    if (pathname.startsWith('/admin/sales-flow') || pathname.startsWith('/admin/workflow') || 
        pathname.startsWith('/admin/policies') || pathname.startsWith('/admin/pain-points') || 
        pathname.startsWith('/admin/discount-codes')) {
      return 'sales-admin';
    }
    if (pathname.startsWith('/admin/products') || pathname.startsWith('/admin/tier-features')) {
      return 'product-admin';
    }
    if (pathname.startsWith('/admin/payments')) {
      return 'payments-admin';
    }
    if (pathname.startsWith('/admin/users') || pathname.startsWith('/admin/ghl-sync') || 
        pathname.startsWith('/admin/role-requests') || pathname.startsWith('/admin/role-manager') || 
        pathname.startsWith('/admin/audit-logs') || pathname.startsWith('/admin/database')) {
      return 'user-admin';
    }
    return undefined;
  }, []);
  
  // Get initial open section for hook initialization
  const getInitialOpenSection = (): string | undefined => {
    return getSectionForRoute(location.pathname);
  };
  
  const {
    expandSection,
    collapseSection,
    isSectionOpen,
    registerSection,
  } = useSmartCollapse({
    containerRef: sidebarContentRef as React.RefObject<HTMLElement>,
    enabled: true,
    initialOpenSections: getInitialOpenSection() ? [getInitialOpenSection()!] : [],
    onCollapse: (sectionId) => {
      setAriaLiveMessage(`${sectionId} section collapsed`);
      setTimeout(() => setAriaLiveMessage(''), 1000);
    },
    onExpand: (sectionId) => {
      setAriaLiveMessage(`${sectionId} section expanded`);
      setTimeout(() => setAriaLiveMessage(''), 1000);
    },
  });

  // Handle section toggle with element registration
  const handleSectionToggle = (sectionId: string, isOpen: boolean, element?: HTMLElement | null) => {
    if (element) {
      registerSection(sectionId, element);
    }
    
    if (isOpen) {
      expandSection(sectionId, element || undefined);
      // Ensure section scrolls into view after expansion
      // The smart collapse hook handles this, but we add a fallback for edge cases
      if (element && sidebarContentRef.current) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            const stillOverflows = detectOverflow(element, sidebarContentRef.current!);
            if (stillOverflows) {
              // Section doesn't fit even after smart collapse - scroll it into view
              const elementTop = element.offsetTop;
              const elementHeight = element.offsetHeight;
              const containerHeight = sidebarContentRef.current!.clientHeight;
              
              if (elementHeight > containerHeight) {
                // Element is taller than viewport - scroll to show top
                sidebarContentRef.current!.scrollTo({
                  top: elementTop,
                  behavior: 'smooth',
                });
              } else {
                // Use scrollIntoViewIfNeeded for optimal positioning
                scrollIntoViewIfNeeded(element, sidebarContentRef.current!, {
                  behavior: 'smooth',
                  block: 'nearest',
                });
              }
            }
          });
        }, 350); // Wait for collapse animations to complete
      }
    } else {
      collapseSection(sectionId);
    }
  };

  // Map to store menu item refs for scroll-to-focus
  const menuItemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Register menu item ref
  const registerMenuItemRef = useCallback((itemId: string) => (element: HTMLElement | null) => {
    if (element) {
      menuItemRefs.current.set(itemId, element);
    } else {
      menuItemRefs.current.delete(itemId);
    }
  }, []);

  // Track the previous route to detect actual navigation changes
  const prevPathnameRef = useRef<string>(location.pathname);
  
  // Scroll active menu item into view when route changes and manage section open/close state
  useEffect(() => {
    if (!sidebarContentRef.current) return;

    const prevPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;
    
    // Only process if route actually changed (not just re-render)
    if (prevPathname === currentPathname) {
      return;
    }
    
    prevPathnameRef.current = currentPathname;

    // Determine which section should be open based on current route
    const targetSectionId = getSectionForRoute(currentPathname);
    const prevTargetSectionId = getSectionForRoute(prevPathname);
    
    // Only auto-collapse sections when navigating to a different admin section
    // This fixes the bug where sections stay open, but doesn't interfere with manual toggles
    if (targetSectionId && prevTargetSectionId && targetSectionId !== prevTargetSectionId) {
      // Navigating from one admin section to another - collapse the previous section
      if (isSectionOpen(prevTargetSectionId)) {
        collapseSection(prevTargetSectionId);
      }
    } else if (!targetSectionId && prevTargetSectionId) {
      // Navigating away from admin routes entirely - collapse the previous section
      if (isSectionOpen(prevTargetSectionId)) {
        collapseSection(prevTargetSectionId);
      }
    }
    
    // Expand the target section if it exists and isn't already open
    if (targetSectionId && !isSectionOpen(targetSectionId)) {
      const element = document.querySelector(`[data-section-id="${targetSectionId}"]`) as HTMLElement;
      if (element) {
        expandSection(targetSectionId, element);
      }
    }

    // Find the active menu item for scrolling
    const allItems = [...navigationItems, ...salesAdminItems, ...productAdminItems, ...paymentsAdminItems, ...userAdminItems];
    const activeItem = allItems.find(item => {
      const path = item.url;
      return location.pathname === path || location.pathname.startsWith(path + '/');
    });

    if (activeItem) {
      // If the active item is in an admin section, wait for section expansion before scrolling
      const adminSectionMap: Record<string, string> = {};
      salesAdminItems.forEach(item => { adminSectionMap[item.id] = 'sales-admin'; });
      productAdminItems.forEach(item => { adminSectionMap[item.id] = 'product-admin'; });
      paymentsAdminItems.forEach(item => { adminSectionMap[item.id] = 'payments-admin'; });
      userAdminItems.forEach(item => { adminSectionMap[item.id] = 'user-admin'; });

      const sectionId = adminSectionMap[activeItem.id];
      if (sectionId && targetSectionId === sectionId) {
        // Wait for section to expand before scrolling
        setTimeout(() => {
          scrollActiveItemIntoView(activeItem.id);
        }, 350); // Match collapse animation duration
      } else {
        // Scroll immediately for non-admin items or if section is already open
        scrollActiveItemIntoView(activeItem.id);
      }
    }
  }, [location.pathname, navigationItems, salesAdminItems, productAdminItems, paymentsAdminItems, userAdminItems, isSectionOpen, expandSection, collapseSection, getSectionForRoute]);

  // Helper function to scroll menu item into view
  const scrollActiveItemIntoView = (itemId: string) => {
    const itemElement = menuItemRefs.current.get(itemId);
    if (itemElement && sidebarContentRef.current) {
      requestAnimationFrame(() => {
        scrollIntoViewIfNeeded(itemElement, sidebarContentRef.current!, {
          behavior: 'smooth',
          block: 'nearest'
        });
      });
    }
  };

  // Enhanced navigation handler with scroll-to-focus
  const handleNavigate = (url: string, itemId?: string) => {
    navigate(url);
    if (itemId) {
      // Scroll after navigation
      setTimeout(() => {
        scrollActiveItemIntoView(itemId);
      }, 100);
    }
  };

  return <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-border">
        <div 
          className={`flex items-center justify-center ${open ? 'p-4' : 'p-0'} ${!open ? 'cursor-pointer' : ''}`}
          onClick={!open ? () => setOpen(true) : undefined}
        >
          {open ? (
            <img src={logoPlain} alt="TowPilot" className="transition-all duration-200 h-16 w-auto" />
          ) : (
            <img 
              src="/favicon.svg" 
              alt="TowPilot" 
              className="transition-all duration-200 h-8 w-8" 
            />
          )}
        </div>
      </SidebarHeader>

      <SidebarContent ref={sidebarContentRef as React.RefObject<HTMLDivElement>} className="space-y-0">
        {/* ARIA live region for accessibility announcements */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {ariaLiveMessage}
        </div>
        <SidebarGroup className="py-2">
          <SidebarGroupContent>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={navigationItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
                <SidebarMenu className="space-y-1">
                  {navigationItems.map(item => <SortableMenuItem key={item.id} item={item} isActive={isActive(item.url)} onClick={() => handleNavigate(item.url, item.id)} itemRef={registerMenuItemRef(item.id)} />)}
                </SidebarMenu>
              </SortableContext>
            </DndContext>
          </SidebarGroupContent>
        </SidebarGroup>

        {showAdminNav && <>
            {open && <Separator className="my-2" />}
            
            <SidebarGroup className="py-2">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAdminSectionDragEnd}>
                <SortableContext items={adminSections.map(section => section.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-0">
                    {adminSections.map(section => (
                      <SortableAdminSection 
                        key={section.id} 
                        section={section} 
                        isActive={isActive} 
                        navigate={navigate} 
                        onDragEnd={e => handleAdminDragEnd(e, section.id)}
                        isOpen={isSectionOpen(section.id)}
                        onOpenChange={(open) => {
                          const element = document.querySelector(`[data-section-id="${section.id}"]`) as HTMLElement;
                          handleSectionToggle(section.id, open, element);
                        }}
                        sectionRef={(element) => registerSection(section.id, element)}
                        onItemClick={handleNavigate}
                        registerMenuItemRef={registerMenuItemRef}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </SidebarGroup>
          </>}
      </SidebarContent>

      <SidebarFooter className="border-t border-border">
        {open ? <div className="p-4 space-y-3">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={() => navigate('/profile')} className="h-auto py-2">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 overflow-hidden">
                    <p className="text-sm font-medium truncate">
                      {profile?.full_name || user?.email}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {userRole?.replace('_', ' ')}
                    </p>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            <Separator />
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={signOut} variant="ghost">
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div> : <div className="p-2">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={() => navigate('/profile')} tooltip="Profile" className="h-auto py-1">
                  <Avatar className="h-8 w-8 mx-auto">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={signOut} variant="ghost" tooltip="Sign Out">
                  <LogOut className="h-4 w-4" />
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>}
      </SidebarFooter>
    </Sidebar>;
}